// Shared date/time formatting for the Notifications and Chats screens.
//
// House rules (client spec) — all of them live here so the two screens can
// never drift apart:
//
//   Notification section headers
//     Order: Today, Yesterday, Last 7 Days, Last 30 Days, Earlier.
//     Evaluated top-down, FIRST MATCH WINS — an item appears in exactly one
//     bucket. Last 7 Days = days 2–7, Last 30 Days = days 8–30: trailing day
//     windows, NOT calendar weeks/months. Empty buckets do not render.
//
//   Notification row timestamps
//     The header already supplies the date, so the row only has to disambiguate
//     WITHIN its bucket: Today → 14m / 2h, Yesterday → 8:43 AM, Last 7 Days →
//     Mon, Last 30 Days → Jul 14, Earlier → Jul 14, 2025 (with the year).
//
//   Chat-list timestamps
//     The chat list is flat — no headers — so each timestamp must stand alone.
//     Under 24h → 14m / 2h, 1–6 days → Mon, this year → Jul 14, prior years →
//     Jul 14, 2025. NEVER a bare clock time here: "8:43 AM" in a headerless
//     list reads as "today".
//
//   Thread separators / bubble clocks
//     A separator labels every message below it until the next separator:
//     Today, Yesterday, a full weekday name inside the last 7 days, then a full
//     date (April 30, 2026). Bubble times are clock-only (7:19 AM).
//
//   "1mo" / "2mo" never appear anywhere.

const MS_MINUTE = 60 * 1000;
const MS_HOUR = 60 * MS_MINUTE;
const MS_DAY = 24 * MS_HOUR;

const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Parse whatever the API hands us. The backend sends "YYYY-MM-DD HH:mm:ss";
// Chrome parses that as local time but Safari rejects it, so retry with the ISO
// "T" separator. Semantics match the pre-existing `new Date(created_at)` calls
// this file replaces — no timezone re-interpretation was introduced here.
export function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value == null || value === "") return null;
  let d = new Date(value);
  if (Number.isNaN(d.getTime()) && typeof value === "string") {
    d = new Date(value.replace(" ", "T"));
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Whole calendar days between two dates (0 = same day, 1 = yesterday). Anchored
// to midnight so "Yesterday" means yesterday, not "24 hours ago".
export function calendarDayDiff(date, now) {
  return Math.round((startOfDay(now) - startOfDay(date)) / MS_DAY);
}

// "now" / "14m" / "2h". Only ever used where a header or a <24h window already
// establishes the day, so it never needs a day/month unit.
export function relativeShort(date, now) {
  const diff = Math.max(0, now.getTime() - date.getTime());
  if (diff < MS_MINUTE) return "now";
  if (diff < MS_HOUR) return `${Math.floor(diff / MS_MINUTE)}m`;
  return `${Math.floor(diff / MS_HOUR)}h`;
}

// "8:43 AM"
export function clockTime(date) {
  const hours24 = date.getHours();
  const hours = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes} ${hours24 >= 12 ? "PM" : "AM"}`;
}

export function weekdayShort(date) {
  return WEEKDAYS_SHORT[date.getDay()];
}

export function weekdayLong(date) {
  return WEEKDAYS_LONG[date.getDay()];
}

// "Jul 14"
export function monthDay(date) {
  return `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}`;
}

// "Jul 14, 2025"
export function monthDayYear(date) {
  return `${monthDay(date)}, ${date.getFullYear()}`;
}

// "April 30, 2026"
export function longDate(date) {
  return `${MONTHS_LONG[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

// ---------------------------------------------------------------- notifications

// Header order IS the evaluation order. Exported so the screen renders buckets
// in spec order without re-stating it.
export const NOTIFICATION_BUCKETS = [
  "Today",
  "Yesterday",
  "Last 7 Days",
  "Last 30 Days",
  "Earlier",
];

export function notificationBucket(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return "Earlier";
  const days = calendarDayDiff(date, now);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days <= 7) return "Last 7 Days"; // days 2–7
  if (days <= 30) return "Last 30 Days"; // days 8–30
  return "Earlier";
}

export function notificationTimestamp(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return "";
  switch (notificationBucket(date, now)) {
    case "Today":
      return relativeShort(date, now);
    case "Yesterday":
      return clockTime(date);
    case "Last 7 Days":
      return weekdayShort(date);
    case "Last 30 Days":
      return monthDay(date);
    default:
      return monthDayYear(date);
  }
}

// Group into spec-ordered buckets, dropping empties. Returns
// [{ bucket, items }] — a bucket with nothing in it never reaches the renderer,
// so it can't render a header or an empty state.
export function groupNotificationsByBucket(
  items,
  getDate = (n) => n?.created_at,
  now = new Date()
) {
  const byBucket = new Map(NOTIFICATION_BUCKETS.map((b) => [b, []]));
  (items || []).forEach((item) => {
    byBucket.get(notificationBucket(getDate(item), now)).push(item);
  });
  return NOTIFICATION_BUCKETS.filter((b) => byBucket.get(b).length > 0).map(
    (bucket) => ({ bucket, items: byBucket.get(bucket) })
  );
}

// ----------------------------------------------------------------------- chats

// Flat-list timestamp: must stand alone, never a bare clock time.
export function chatListTimestamp(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return "";
  const elapsed = Math.max(0, now.getTime() - date.getTime());
  if (elapsed < MS_DAY) return relativeShort(date, now);
  if (calendarDayDiff(date, now) <= 6) return weekdayShort(date);
  if (date.getFullYear() === now.getFullYear()) return monthDay(date);
  return monthDayYear(date);
}

// Thread date separator: Today / Yesterday / Thursday / April 30, 2026.
export function threadSeparatorLabel(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return "";
  const days = calendarDayDiff(date, now);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days <= 6) return weekdayLong(date);
  return longDate(date);
}

// "Last active Jul 14" — the offline half of the presence status line. Returns
// null when there is no timestamp to show, so the caller renders nothing rather
// than inventing a date.
export function lastActiveLabel(value, now = new Date()) {
  const date = toDate(value);
  if (!date) return null;
  return `Last active ${
    date.getFullYear() === now.getFullYear() ? monthDay(date) : monthDayYear(date)
  }`;
}

// Split a chronological message list into runs: consecutive messages from the
// same sender under the same date separator. Avatars render on the FIRST bubble
// of an incoming run; clock times render under the LAST bubble of any run.
// Returns the input list annotated in place-safe copies.
export function annotateMessageRuns(
  messages,
  getSenderId,
  getDate = (m) => m?.created_at,
  now = new Date()
) {
  const list = messages || [];
  return list.map((message, i) => {
    const date = toDate(getDate(message));
    const prev = list[i - 1];
    const next = list[i + 1];
    const prevDate = prev ? toDate(getDate(prev)) : null;
    const nextDate = next ? toDate(getDate(next)) : null;

    // A separator is needed whenever the calendar day changes (and before the
    // first message).
    const startsNewDay =
      !prevDate || !date || calendarDayDiff(prevDate, now) !== calendarDayDiff(date, now);
    const nextStartsNewDay =
      !nextDate || !date || calendarDayDiff(nextDate, now) !== calendarDayDiff(date, now);

    const sameSenderAsPrev =
      !!prev && String(getSenderId(prev)) === String(getSenderId(message));
    const sameSenderAsNext =
      !!next && String(getSenderId(next)) === String(getSenderId(message));

    return {
      message,
      date,
      separator: startsNewDay ? threadSeparatorLabel(date, now) : null,
      isRunStart: startsNewDay || !sameSenderAsPrev,
      isRunEnd: nextStartsNewDay || !sameSenderAsNext,
    };
  });
}
